import React, { useEffect, useContext } from "react";
import { SearchModel } from "types/qbank/searchModel";
import { Loader } from "components/ui";
import { useFilter } from "hooks/filter";
import { useApi } from "hooks/api";
import { useInfiniteQuery, useQuery } from "react-query";
import { Box, List, Typography } from "@mui/material";
import DetailDialog from "./detailModal/DetailDialog";
import { useMediaModal } from "hooks/mediaModal";
import MediaCard from "./MediaCard";
import MediaList from "./MediaList";
import { Media } from "types/qbank/media";
import { TotalHitsContext } from "../../hooks/TotalHits";


export default function SearchResult() {
  const { modal, setModal } = useMediaModal();
  const { search, getDeploymentSite } = useApi();
  const { searchState, contentView } = useFilter();
  const totalHitsContext = useContext(TotalHitsContext);

  const _searchLimit = 40;

  const deploymentSiteData = useQuery(
    ["getDeploymentSite", searchState.currentCategory.deploymentSiteId],
    () =>
      getDeploymentSite(parseInt(searchState.currentCategory.deploymentSiteId)),
    {
      staleTime: Infinity,
      retry: false,
    }
  );

  const handleDoubleClick = (media: Media, event: React.MouseEvent<HTMLDivElement>) => {
    setModal({ open: true, media: media })
  };

  const doSearch = ({ pageParam = 0 }: any) => {
    let searchParams: SearchModel = {
      limit: _searchLimit,
      offset: pageParam,
      ...searchState.search,
    };

    return search(searchParams);
  };

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
  } = useInfiniteQuery(["search", searchState.search], doSearch, {
    retry: 0,
    staleTime: 50000,
    getNextPageParam: (lastPage, pages) => {
      return lastPage.limit + lastPage.offset;
    },
  });

  useEffect(() => {
    if (data !== undefined) {
      const _windowHeight = window.innerHeight * 1.5;
      const lastPage = data.pages[data.pages.length - 1];
      const hasMore = lastPage.offset + lastPage.limit < lastPage.totalHits;

      function handleScrollPopUp(e: Event) {
        const scrollElement = e.target as HTMLDivElement;
        if (
          scrollElement.scrollTop >
          scrollElement.offsetHeight - _windowHeight &&
          hasMore &&
          !isFetchingNextPage
        ) {
          fetchNextPage();
        }
      };

      const scrollableElement = document.querySelector(".connector");

      if (scrollableElement) {
          scrollableElement.addEventListener("scroll", handleScrollPopUp);
      }
      if (totalHitsContext) {
        totalHitsContext.setTotalHits(data.pages[0]?.totalHits);
      }
      return () => {
        if (scrollableElement) {
          scrollableElement.removeEventListener("scroll", handleScrollPopUp);
        }
      };
    }
  }, [data, fetchNextPage, hasNextPage, isFetchingNextPage, totalHitsContext]);

  if (
    isLoading ||
    isError ||
    !data ||
    deploymentSiteData.isLoading ||
    deploymentSiteData.isError ||
    !deploymentSiteData.data
  ) {
    return <Loader />;
  }

  const getTotalCount = () => {
    if (data && data.pages && data.pages.length > 0) {
      return data.pages[0].totalHits;
    }
    return 0;
  }
  return (
    <>
    <React.Fragment>
      {getTotalCount() === 0 ? (
            <Box mt={2} sx={{ flexGrow: 1 }}>
              <Typography variant="h4" textAlign="center">
                No results!
              </Typography>
            </Box>
          ) : (
            <>
              {contentView === "grid" ? (
                  <Box mt={2} sx={{
                    flexGrow: 1
                  }}>
                    <Box
                      sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: 'repeat(auto-fill, minmax(150px, 1fr))',
                        sm: 'repeat(auto-fill, minmax(184px , 1fr))',
                        md: 'repeat(auto-fill, minmax(211px , 1fr))',
                        lg: 'repeat(auto-fill, minmax(227px , 1fr))',
                      },
                        gap: '8px',
                      }}
                    >
                  {data.pages.map((gridPage, i) => (
                    gridPage.results?.map((item) => (
                      <Box
                        key={item.mediaId}
                        sx={{ aspectRatio: '19 / 20' }}>
                      <MediaCard
                        key={item.mediaId}
                        media={item}
                        deploymentSite={deploymentSiteData.data}
                      />
                      </Box>
                      ))
                    ))}
                    </Box>
                </Box>
              ) : (
                <List
                  sx={{
                    width: "100%",
                    bgcolor: "background.paper",
                  }}
                >
                  {data.pages.map((listPage, i) => (
                    listPage.results?.map((item) => (
                    <MediaList
                      key={item.mediaId}
                      media={item}
                      deploymentSite={deploymentSiteData.data}
                    />
                      ))
                    ))}
                </List>
              )}
            </>
          )}
    </React.Fragment>

      {modal.open && <DetailDialog deploymentSite={deploymentSiteData.data} onDoubleClick={handleDoubleClick} />}
    </>
)
}
